import { AxiosInstance } from 'axios';
import { mutateProfile } from 'hooks';
import { BaseApi } from 'services/api/base-api';
import { ApiResult } from 'services/api/result.type';
import { Category } from 'enum/categories';
import { UserClassification } from 'enum/classification.enum';
import { UserGender } from 'enum/gender.enum';
import { UpdateDeviceTokenRequestDto } from '../auth/dto/update-device-token.request.dto';
import {
  GetProfileResponse,
  SetNameRequest,
  UpdateCommunicationSettingsRequest,
} from './dto';

export class ProfileApi extends BaseApi {
  public constructor (protected readonly api: AxiosInstance) {
    super();

    this.getProfile = this.getProfile.bind(this);
    this.updateFavoriteCategories = this.updateFavoriteCategories.bind(this);
    this.updateCommunicationSettings = this.updateCommunicationSettings.bind(
      this,
    );
    this.setName = this.setName.bind(this);
    this.updateZipCode = this.updateZipCode.bind(this);
    this.updatePersonalEmail = this.updatePersonalEmail.bind(this);
    this.updateMobileNumber = this.updateMobileNumber.bind(this);
    this.updateGraduationDate = this.updateGraduationDate.bind(this);
    this.updateBirthday = this.updateBirthday.bind(this);
    this.leaveOrganization = this.leaveOrganization.bind(this);
    this.changePassword = this.changePassword.bind(this);

    this.confirmAddEmail = this.confirmAddEmail.bind(this);

    this.addEmail = this.addEmail.bind(this);
    this.removeEmail = this.removeEmail.bind(this);
  }

  public async getProfile (): ApiResult<GetProfileResponse> {
    return await this.get<GetProfileResponse>(
      '/profile',
      {},
      { redirectOn401: false },
    );
  }

  public async updateFavoriteCategories (
    categories: Category[],
  ): ApiResult<void> {
    const result = await this.patch<void>('/profile/favorite-categories', {
      categories,
    });
    void mutateProfile();
    return result;
  }

  public async updateCommunicationSettings (
    newSettings: UpdateCommunicationSettingsRequest,
  ): ApiResult<void> {
    const result = await this.patch<void>(
      '/profile/communication-settings',
      newSettings,
    );
    void mutateProfile();
    return result;
  }

  public async setName ({
    firstName,
    lastName,
  }: SetNameRequest): ApiResult<void> {
    const result = await this.put<void>('/profile/name', {
      firstName,
      lastName,
    });
    void mutateProfile();
    return result;
  }

  public async updateZipCode (zipCode: string): ApiResult<void> {
    const result = await this.put<void>('/profile/zipCode', {
      zipCode,
    });
    void mutateProfile();
    return result;
  }

  public async updateDeviceToken (
    data: UpdateDeviceTokenRequestDto,
  ): ApiResult<void> {
    return await this.put('/profile/device-token', data);
  }

  public async updatePersonalEmail (email: string): ApiResult<void> {
    const result = await this.put<void>('/profile/personalEmail', {
      email,
    });
    void mutateProfile();
    return result;
  }

  public async updateMobileNumber (mobileNumber: string): ApiResult<void> {
    const result = await this.put<void>('/profile/mobileNumber', {
      mobileNumber,
    });
    void mutateProfile();
    return result;
  }

  public async updateGraduationDate (year: number): ApiResult<void> {
    const result = await this.put<void>('/profile/graduationDate', {
      year,
    });
    void mutateProfile();
    return result;
  }

  public async updateClassification (
    classification: UserClassification,
  ): ApiResult<void> {
    const result = await this.put<void>('/profile/classification', {
      classification,
    });
    void mutateProfile();
    return result;
  }

  public async updateGpa (gpa: number): ApiResult<void> {
    const result = await this.put<void>('/profile/gpa', {
      gpa,
    });
    void mutateProfile();
    return result;
  }

  public async updateMajors (majors: string[]): ApiResult<void> {
    const result = await this.put<void>('/profile/majors', {
      majors,
    });
    void mutateProfile();
    return result;
  }

  public async updateGender (gender: UserGender): ApiResult<void> {
    const result = await this.put<void>('/profile/gender', {
      gender,
    });
    void mutateProfile();
    return result;
  }

  public async updateBirthday (birthday: Date): ApiResult<void> {
    const result = await this.put<void>('/profile/birthday', {
      birthday,
    });
    void mutateProfile();
    return result;
  }

  public async leaveOrganization (organizationId: string): ApiResult<void> {
    const result = await this.post<void>('/profile/leaveOrganization', {
      organizationId,
    });
    void mutateProfile();
    return result;
  }

  public async changePassword (
    currentPassword: string,
    newPassword: string,
  ): ApiResult<void> {
    return await this.post<void>(
      '/profile/password',
      {
        currentPassword,
        newPassword,
      },
      {},
      {
        redirectOn401: false,
      },
    );
  }

  public async confirmAddEmail (token: string): ApiResult<void> {
    return await this.post<void>('/profile/emails/confirm', {
      token,
    });
  }

  public async addEmail (email: string): ApiResult<void> {
    return await this.post<void>('/profile/emails', {
      email,
    });
  }

  public async removeEmail (email: string): ApiResult<void> {
    return await this.delete<void>(
      `/profile/emails/${encodeURIComponent(email)}`,
    );
  }
}
